import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const clinicApi = createApi({
  reducerPath: "clinics",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: ["Clinics"],
  endpoints: (builder) => ({
    listClinics: builder.mutation({
      query: ({ token }) => {
        return {
          url: "/api/admin/clinics/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    getClinicSummary: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/detail/summary",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    getClinicInfo: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/detail/info",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    getClinicWorkingHours: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/detail/working-hours",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    getClinicAppearance: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/detail/appearance",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    sendClinicVerificationCode: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/send-verification-code",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    verifyAndCreateClinic: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/verify-and-create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    createClinic: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/verify-and-create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    // ─── Clinic-side görünüm (ClinicStaff yöneticisi için) ──────────
    getMyClinicAppearance: builder.mutation({
      query: ({ token }) => {
        return {
          url: "/api/clinic/appearance/get",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        };
      },
    }),
    updateMyClinicAppearance: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/appearance/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    uploadMyClinicLogo: builder.mutation({
      query: ({ token, file, logoType }) => {
        const formData = new FormData();
        formData.append("logo", file);
        if (logoType) formData.append("logoType", logoType);
        return {
          url: "/api/clinic/appearance/logo/upload",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        };
      },
    }),
    listClinicStaff: builder.mutation({
      query: ({ token, clinicId }) => {
        return {
          url: "/api/admin/clinics/staff/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { clinicId },
        };
      },
    }),
    // ClinicStaff yöneticisi için kendi kliniğinin personel listesi.
    // Server response { data: { users: [...] } } admin'in `staff` anahtarına
    // çevriliyor ki UI bileşeni aynı şekli alabilsin.
    listMyClinicStaff: builder.mutation({
      query: ({ token, clinicId }) => {
        return {
          url: "/api/clinic/users/listUsers",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { clinicId },
        };
      },
      transformResponse: (response) => {
        const users = response?.data?.users || [];
        return {
          ...response,
          data: {
            ...(response?.data || {}),
            staff: users,
          },
        };
      },
    }),
    createClinicStaff: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/staff/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    updateClinicStaff: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/staff/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    // ClinicStaff yöneticisi için kendi kliniğine personel oluşturma/güncelleme.
    // clinicId asla client'tan gönderilmez — sunucu req.user.clinic'ten alır.
    createMyClinicStaff: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/users/staff/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    updateMyClinicStaff: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/users/staff/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    updateClinic: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    updateClinicInfo: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update/info",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    updateClinicWorkingHours: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update/working-hours",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    updateClinicAppearance: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update/appearance",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    setClinicStatus: builder.mutation({
      query: ({ token, clinicId, status }) => {
        return {
          url: "/api/admin/clinics/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { clinicId, status },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    uploadClinicLogo: builder.mutation({
      query: ({ token, clinicId, file, logoType }) => {
        const formData = new FormData();
        formData.append("clinicId", clinicId);
        formData.append("logo", file);
        if (logoType) formData.append("logoType", logoType);
        return {
          url: "/api/admin/clinics/logo/upload",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        };
      },
    }),
    hardDeleteClinic: builder.mutation({
      query: ({ token, clinicId, password }) => {
        return {
          url: "/api/admin/clinics/hard-delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { clinicId, password },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    updateClinicName: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update/name",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    sendEmailChangeCode: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update/email/send-code",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    verifyEmailChange: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update/email/verify",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["Clinics"],
    }),
    updateClinicContact: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update/contact",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    updateClinicTax: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/clinics/update/tax",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
    }),
    // ── Modüller (profil, izin verilen modüller, terim seti) ──
    getClinicModules: builder.mutation({
      query: ({ token, ...payload }) => ({
        url: "/api/admin/clinics/modules/get",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: { ...payload },
      }),
    }),
    updateClinicModules: builder.mutation({
      query: ({ token, ...payload }) => ({
        url: "/api/admin/clinics/modules/update",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: { ...payload },
      }),
    }),
    // Klinik yöneticisinin kendi kliniği (clinicId sunucuda oturumdan)
    getMyClinicModules: builder.mutation({
      query: ({ token }) => ({
        url: "/api/clinic/modules/get",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: {},
      }),
    }),
    updateMyClinicModules: builder.mutation({
      query: ({ token, ...payload }) => ({
        url: "/api/clinic/modules/update",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: { ...payload },
      }),
    }),
  }),
});

export const {
  useListClinicsMutation,
  useCreateClinicMutation,
  useGetClinicSummaryMutation,
  useGetClinicInfoMutation,
  useGetClinicWorkingHoursMutation,
  useGetClinicAppearanceMutation,
  useSendClinicVerificationCodeMutation,
  useVerifyAndCreateClinicMutation,
  useListClinicStaffMutation,
  useListMyClinicStaffMutation,
  useGetMyClinicAppearanceMutation,
  useUpdateMyClinicAppearanceMutation,
  useUploadMyClinicLogoMutation,
  useCreateClinicStaffMutation,
  useUpdateClinicStaffMutation,
  useCreateMyClinicStaffMutation,
  useUpdateMyClinicStaffMutation,
  useUpdateClinicMutation,
  useUpdateClinicInfoMutation,
  useUpdateClinicWorkingHoursMutation,
  useUpdateClinicAppearanceMutation,
  useSetClinicStatusMutation,
  useUploadClinicLogoMutation,
  useHardDeleteClinicMutation,
  useUpdateClinicNameMutation,
  useSendEmailChangeCodeMutation,
  useVerifyEmailChangeMutation,
  useUpdateClinicContactMutation,
  useUpdateClinicTaxMutation,
  useGetClinicModulesMutation,
  useUpdateClinicModulesMutation,
  useGetMyClinicModulesMutation,
  useUpdateMyClinicModulesMutation,
} = clinicApi;
